import { NotificationAuthError, buildNotificationStreamUrl, getNotificationTicket } from '@/lib/notifications-client';
import type { NotificationEventType, NotificationPayload } from '@/types/notifications';

type Handler = (payload: NotificationPayload) => void;

const EVENT_TYPES: NotificationEventType[] = [
  'new_order',
  'conversation_switched_to_manual',
  'conversation_needs_intervention',
];

class NotificationStream {
  private es: EventSource | null = null;
  private handlers = new Set<Handler>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private authFailures = 0;
  private closed = true;

  /**
   * How many consecutive authorization failures to tolerate before giving up. A delegated agent-ui
   * session has no refresh path, so once its token expires every retry returns the same 401 — left
   * unbounded that is one pointless request a minute for the life of the tab. A small budget still
   * absorbs a blip (the auth layer revalidates and may find the session fine) without the loop.
   */
  private static readonly MAX_AUTH_FAILURES = 3;

  async open() {
    if (!this.closed && this.es) return;
    this.closed = false;
    await this.connect();
  }

  close() {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.es?.close();
    this.es = null;
    this.handlers.clear();
    this.attempts = 0;
    this.authFailures = 0;
  }

  subscribe(handler: Handler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  private async connect() {
    if (this.closed) return;
    try {
      const { ticket } = await getNotificationTicket();
      const es = new EventSource(buildNotificationStreamUrl(ticket));
      this.es = es;

      EVENT_TYPES.forEach((type) => {
        es.addEventListener(type, (e) => {
          try {
            const payload = JSON.parse((e as MessageEvent).data) as NotificationPayload;
            this.handlers.forEach((h) => h(payload));
          } catch (err) {
            console.warn('Failed to parse notification payload', err);
          }
        });
      });

      es.addEventListener('heartbeat', () => {});

      es.onopen = () => {
        this.attempts = 0;
        this.authFailures = 0;
      };

      es.onerror = () => this.scheduleReconnect();
    } catch (err) {
      if (err instanceof NotificationAuthError) {
        // The ticket client has already raised the auth event that drives re-authentication. Give
        // that a couple of chances to land, then stop: past this point the session is genuinely
        // unauthorized and every further attempt returns the same status.
        this.authFailures += 1;
        if (this.authFailures >= NotificationStream.MAX_AUTH_FAILURES) {
          console.warn('Notification stream not authorized — stopping until re-authenticated', err);
          this.closed = true;
          return;
        }
      }
      console.warn('Notification stream connect failed', err);
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    this.es?.close();
    this.es = null;
    if (this.closed) return;
    const delay = Math.min(3000 * Math.pow(2, this.attempts), 60_000);
    this.attempts += 1;
    this.reconnectTimer = setTimeout(() => void this.connect(), delay);
  }
}

export const notificationStream = new NotificationStream();
